(() => {
  'use strict';
  const widgets = [...document.querySelectorAll('[data-namture-code]')];
  if (!widgets.length) return;
  const messages = JSON.parse(widgets[0].querySelector('[data-code-translations]').textContent);
  const root = window.Shopify?.routes?.root || '/';
  const storageKey = 'namture-cart-code-choice';
  let busy = false;
  let syncing = false;
  let currentCart = null;
  let appliedCodes = [];
  let preference = null;
  try { preference = JSON.parse(sessionStorage.getItem(storageKey)); } catch (_) {}
  const normalize = code => String(code || '').trim().toUpperCase();
  const isWelcome = code => /^WELKOM(?:-|5$|10)/i.test(code);
  const unique = codes => [...new Set(codes.map(normalize).filter(Boolean))];
  const token = cart => String(cart.token || '').split('?')[0];
  const same = (a, b) => unique(a).sort().join(',') === unique(b).sort().join(',');
  const applications = cart => [
    ...(cart.cart_level_discount_applications || []).map(app => app.discount_application || app),
    ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(allocation => allocation.discount_application))
  ];
  function codes(cart) {
    const active = (cart.discount_codes || []).filter(entry => entry.applicable !== false).map(entry => entry.code);
    return unique(active.concat(applications(cart).filter(app => app?.type === 'discount_code').map(app => app.title)));
  }
  function remember(cart, value) {
    preference = { token: token(cart), codes: unique(value), time: Date.now() };
    try { sessionStorage.setItem(storageKey, JSON.stringify(preference)); } catch (_) {}
  }
  function hasPreference(cart) {
    return preference && preference.token === token(cart) && Date.now() - preference.time < 86400000;
  }
  async function request(path, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(root + path, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {})
      });
      if (!response.ok) throw new Error('cart_request_failed');
      return await response.json();
    } finally { clearTimeout(timeout); }
  }
  const read = () => request('cart.js');
  const write = values => request('cart/update.js', { discount: unique(values).join(',') });
  function status(key, error = false) {
    widgets.forEach(widget => {
      const node = widget.querySelector('[data-code-status]');
      node.textContent = messages[key] || '';
      node.hidden = !key;
      node.classList.toggle('is-error', error);
    });
  }
  function setBusy(value) {
    busy = value;
    widgets.forEach(widget => {
      widget.setAttribute('aria-busy', String(value));
      widget.querySelectorAll('button, input').forEach(element => { element.disabled = value; });
    });
    document.querySelectorAll('#CartPageForm [name="checkout"], #CartDrawerForm [name="checkout"], #CartPageForm .js-qty__adjust, #CartDrawerForm .js-qty__adjust, #CartPageForm .js-qty__num, #CartDrawerForm .js-qty__num').forEach(element => {
      if (value && !element.disabled) { element.dataset.codeLocked = 'true'; element.disabled = true; }
      else if (!value && element.dataset.codeLocked) { element.disabled = false; delete element.dataset.codeLocked; }
    });
  }
  function display(values) {
    appliedCodes = unique(values);
    widgets.forEach(widget => {
      const list = widget.querySelector('[data-code-list]');
      list.replaceChildren();
      appliedCodes.forEach(code => {
        const chip = document.createElement('div');
        chip.className = 'namture-cart-code__chip';
        const label = document.createElement('span');
        label.textContent = code;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.dataset.codeRemove = code;
        remove.setAttribute('aria-label', messages.remove + ' ' + code);
        remove.textContent = '×';
        remove.disabled = busy;
        let source = applications(currentCart || {});
        if (!currentCart) {
          try { source = JSON.parse(document.querySelector('[data-code-applications]')?.dataset.applications || '[]'); } catch (_) {}
        }
        const amount = source.filter(app => app?.type === 'discount_code' && normalize(app.title) === code).reduce((sum, app) => sum + Number(app.total_allocated_amount || 0), 0);
        const saving = document.createElement('strong');
        if (amount > 0) saving.textContent = '−' + new Intl.NumberFormat(document.documentElement.lang || 'nl', {style:'currency', currency:currentCart?.currency || widgets[0].dataset.currency}).format(amount / 100);
        chip.append(label, saving, remove);
        list.append(chip);
      });
      const summary = widget.querySelector('summary');
      summary.firstChild.textContent = (appliedCodes.length ? messages.change : messages.add);
    });
  }
  function serverCodes() {
    const source = document.querySelector('[data-code-applications]');
    if (!source) return [];
    try { return unique(JSON.parse(source.dataset.applications).filter(app => app.type === 'discount_code').map(app => app.title)); }
    catch (_) { return []; }
  }
  function renderCart() {
    return new Promise((resolve, reject) => {
      const selector = document.getElementById('CartPageForm') ? '#CartPageForm [data-discounts]' : '#CartDrawerForm [data-discounts]';
      const targets = [...document.querySelectorAll(selector)];
      if (!targets.length) { resolve(); return; }
      const seen = new Set();
      const observer = new MutationObserver(records => {
        records.forEach(record => targets.forEach(target => { if (target === record.target || target.contains(record.target)) seen.add(target); }));
        if (seen.size === targets.length) { clearTimeout(timeout); observer.disconnect(); resolve(); }
      });
      const timeout = setTimeout(() => { observer.disconnect(); reject(new Error('cart_render_failed')); }, 12000);
      targets.forEach(target => observer.observe(target, { childList: true, subtree: true }));
      document.dispatchEvent(new CustomEvent('cart:build'));
    });
  }
  async function change(input, remove = false) {
    if (busy) return;
    const requested = normalize(input);
    if (!requested || requested.includes(',')) { status('enter', true); return; }
    let previous = null;
    let attempted = false;
    setBusy(true);
    status('busy');
    try {
      previous = await read();
      const before = codes(previous);
      const shipping = applications(previous).filter(app => app.type === 'discount_code' && app.target_type === 'shipping_line').map(app => normalize(app.title));
      let next = remove ? before.filter(code => code !== requested) : before.filter(code => isWelcome(requested) ? shipping.includes(code) : !isWelcome(code));
      if (!remove) next.push(requested);
      next = unique(next);
      attempted = true;
      let updated = await write(next);
      if (!remove && !codes(updated).includes(requested)) {
        updated = await write(before);
        currentCart = updated;
        display(codes(updated));
        await renderCart();
        status('invalid', true);
        return;
      }
      currentCart = updated;
      remember(updated, codes(updated));
      display(codes(updated));
      await renderCart();
      widgets.forEach(widget => {
        widget.querySelector('[data-code-input]').value = '';
        widget.querySelector('details').open = false;
      });
      status(remove ? 'removed' : before.some(code => !codes(updated).includes(code)) ? 'replaced' : 'applied');
      document.dispatchEvent(new CustomEvent('namture:discount-updated'));
    } catch (_) {
      if (previous && attempted) {
        try {
          currentCart = await write(codes(previous));
          remember(currentCart, codes(currentCart));
          display(codes(currentCart));
          await renderCart();
        } catch (_) { window.location.reload(); return; }
      }
      status('error', true);
    } finally { setBusy(false); }
  }
  async function sync(enforce = false) {
    if (busy || syncing) return;
    syncing = true;
    let locked = false;
    try {
      let cart = await read();
      if (busy) return;
      let values = codes(cart);
      let needsRender = !same(values, appliedCodes) || cart.total_price !== (currentCart?.total_price ?? Number(widgets[0].dataset.cartTotal));
      if (hasPreference(cart) && !same(values, preference.codes) && enforce) {
        setBusy(true);
        locked = true;
        cart = await write(preference.codes);
        values = codes(cart);
        needsRender = true;
      }
      if (needsRender) {
        if (!locked) { setBusy(true); locked = true; }
        await renderCart();
      }
      currentCart = cart;
      display(values);
      if (cart.item_count === 0 && preference) {
        preference = null;
        try { sessionStorage.removeItem(storageKey); } catch (_) {}
      }
    } catch (_) { if (locked) window.location.reload(); }
    finally { syncing = false; if (locked) setBusy(false); }
  }
  widgets.forEach(widget => {
    widget.querySelector('[data-code-apply]').addEventListener('click', () => change(widget.querySelector('[data-code-input]').value));
    widget.querySelector('[data-code-input]').addEventListener('keydown', event => {
      if (event.key === 'Enter') { event.preventDefault(); event.stopPropagation(); change(event.target.value); }
    });
    widget.addEventListener('click', event => {
      const button = event.target.closest('[data-code-remove]');
      if (button) change(button.dataset.codeRemove, true);
    });
  });
  document.addEventListener('submit', event => {
    if (busy && event.target.matches('#CartPageForm, #CartDrawerForm')) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('drawerOpen.CartDrawer', () => sync());
  document.addEventListener('cart:updated', event => {
    if (!busy && event.detail?.cart) {
      currentCart = event.detail.cart;
      display(codes(currentCart));
    }
  });
  window.addEventListener('pageshow', event => { if (event.persisted) sync(); });
  window.addEventListener('klaviyoForms', event => {
    if (event.detail?.formId !== 'WMaAUY' || !['submit', 'stepSubmit', 'close'].includes(event.detail.type)) return;
    [0, 800, 2000].forEach(delay => setTimeout(() => sync(true), delay));
  });
  display(serverCodes());
  if (widgets.some(widget => Number(widget.dataset.cartCount) > 0)) sync();
})();
